import { FC, useEffect, useRef } from 'react'
import { IconPaths, TipoReceitaSncr } from '_metronic/helpers'
import { useSncr, useSncrContexto } from '../../../../sncr/hooks/useSncr'
import { precisaDeApac, precisaDeLme } from '../../core/documentosSus'
import { CanalEnvio, PreferenciasEnvio } from '../../core/montagem/envio'
import { documentoDaPrescricao } from '../../core/montagem/historico'
import { DocumentoEmAssinatura } from '../../hooks/useAssinaturaDocumentos'
import { EstadoCanal } from '../../hooks/useEnvioPaciente'
import styles from '../../Prescricao.module.scss'
import { Icone } from '../comum/Icone'
import { useRotulos } from '../comum/useRotulos'
import { AcaoPendente, AcoesPendentes } from './AcoesPendentes'
import { DocumentosAssinados } from './DocumentosAssinados'
import { StatusEnvio } from './StatusEnvio'
import { Button } from 'app/components'

type IProps = {
  documentos: DocumentoEmAssinatura[]
  nomePaciente?: string
  atendeSUS: boolean
  idEmpresaSncr?: number
  envio: { preferencias?: PreferenciasEnvio; estados: Partial<Record<CanalEnvio, EstadoCanal>> }
  onReenviar: (canal: CanalEnvio) => void
  onAssinarDeNovo: (ids: number[]) => void
  onGerarSus: (documento: 'lme' | 'apac') => void
  onNovaPrescricao: () => void
}

/** Fim da jornada: documentos assinados, envio por canal e o que ficou pendente. */
export const ResultadoPrescricao: FC<IProps> = ({
  documentos,
  nomePaciente,
  atendeSUS,
  idEmpresaSncr,
  envio,
  onReenviar,
  onAssinarDeNovo,
  onGerarSus,
  onNovaPrescricao,
}) => {
  const { t, tituloDocumento } = useRotulos()
  const { data: contexto, refetch } = useSncrContexto(idEmpresaSncr)
  const { showRetiradaModal } = useSncr()
  // Ao chegar aqui o modal de assinatura fechou e o botão que o abriu já não existe: o foco vai
  // para o título, e o leitor de tela anuncia o resultado.
  const tituloRef = useRef<HTMLHeadingElement>(null)
  useEffect(() => {
    tituloRef.current?.focus()
  }, [])

  const assinados = documentos.filter((doc) => doc.estado === 'assinado')
  const comErro = documentos.filter((doc) => doc.estado === 'erro')
  const lmePendente = atendeSUS ? assinados.filter((doc) => precisaDeLme(doc.prescricao)) : []
  const apacPendente = atendeSUS ? assinados.filter((doc) => precisaDeApac(doc.prescricao)) : []
  const tiposUsados = new Set(
    assinados.map((doc) => doc.prescricao.tipoReceitaSncr).filter(Boolean) as TipoReceitaSncr[]
  )
  const saldosBaixos = contexto?.habilitado
    ? contexto.saldos.filter((saldo) => saldo.alerta && tiposUsados.has(saldo.tipo))
    : []

  const acoes: AcaoPendente[] = [
    ...comErro.map((doc) => ({
      chave: `erro-${doc.prescricao.id}`,
      titulo: tituloDocumento(documentoDaPrescricao(doc.prescricao)),
      descricao: t('prescricao.resultado.nao_assinado').toLowerCase(),
      rotuloAcao: t('prescricao.resultado.assinar_agora'),
      executar: () => onAssinarDeNovo([doc.prescricao.id]),
      grave: true,
    })),
    ...(lmePendente.length > 0
      ? [
          {
            chave: 'lme',
            titulo: t('prescricao.resultado.lme_pendente'),
            descricao: t('prescricao.resultado.documentos_qtd', { quantidade: lmePendente.length }),
            rotuloAcao: t('prescricao.sus.gerar_lme'),
            executar: () => onGerarSus('lme'),
          },
        ]
      : []),
    ...(apacPendente.length > 0
      ? [
          {
            chave: 'apac',
            titulo: t('prescricao.resultado.apac_pendente'),
            descricao: t('prescricao.resultado.documentos_qtd', {
              quantidade: apacPendente.length,
            }),
            rotuloAcao: t('prescricao.sus.gerar_apac'),
            executar: () => onGerarSus('apac'),
          },
        ]
      : []),
    ...saldosBaixos.map((saldo) => ({
      chave: `sncr-${saldo.tipo}`,
      titulo: t('prescricao.resultado.saldo_baixo', { tipo: saldo.tipo }),
      descricao: t('prescricao.resultado.restam', { quantidade: saldo.saldo }),
      rotuloAcao: t('sncr.alerta.retirar'),
      executar: async () => {
        if (!idEmpresaSncr || !contexto) return
        if (await showRetiradaModal(idEmpresaSncr, saldo.tipo, contexto)) refetch()
      },
    })),
  ]

  return (
    <div className='d-flex flex-column'>
      <section className='card card-bordered mb-5' role='status'>
        <div className='card-body d-flex flex-wrap align-items-center gap-4'>
          <Icone icone={IconPaths.Check} className={`${styles.estadoOk} fs-2tx`} />
          <div className='flex-grow-1 d-flex flex-column'>
            <h2 ref={tituloRef} tabIndex={-1} className='fs-3 fw-bold m-0'>
              {t('prescricao.resultado.titulo', { quantidade: assinados.length })}
            </h2>
            <span className={styles.textoSecundario}>
              {t('prescricao.resultado.subtitulo', { paciente: nomePaciente ?? '' })}
            </span>
          </div>
          <Button className='btn-primary' onClick={onNovaPrescricao}>
            {t('prescricao.resultado.nova')}
          </Button>
        </div>
      </section>

      <AcoesPendentes acoes={acoes} />

      <div className='row g-5'>
        <div className='col-xl-8'>
          <DocumentosAssinados documentos={documentos} />
        </div>
        <div className='col-xl-4'>
          <StatusEnvio
            preferencias={envio.preferencias}
            estados={envio.estados}
            onReenviar={onReenviar}
          />
        </div>
      </div>
    </div>
  )
}
